import { useEffect, useState } from "react";
import {
  sapClearCompanyConfig,
  sapCompaniesList,
  sapCompanyEntity,
  sapCompanyLogout,
  sapCompanyPing,
  sapCompanyStatus,
  sapGlobalConfig,
  sapSetCompanyConfig,
  type SapCompanyListItem,
  type SapCompanyStatus,
  type SapEntityResponse,
  type SapGlobalConfig,
  type SapPingResponse,
} from "../api";

type QueryState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ok"; data: SapEntityResponse }
  | { kind: "error"; message: string };

export function Sap() {
  const [globalCfg, setGlobalCfg] = useState<SapGlobalConfig | null>(null);
  const [companies, setCompanies] = useState<SapCompanyListItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const loadAll = () => {
    setError(null);
    Promise.all([sapGlobalConfig(), sapCompaniesList()])
      .then(([cfg, list]) => {
        setGlobalCfg(cfg);
        setCompanies(list.items);
        if (!selectedId && list.items.length > 0) {
          const withSap = list.items.find((c) => c.sap);
          const fallback = list.items[0];
          setSelectedId(withSap?.id ?? fallback?.id ?? "");
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(loadAll, []);

  const selected = companies?.find((c) => c.id === selectedId) ?? null;

  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>SAP Business One · Service Layer</h2>
            <p className="meta">
              Cada empresa mantiene su propia CompanyDB / usuario / password.
              El host del Service Layer es común a todas.
            </p>
          </div>
          <button type="button" onClick={loadAll}>↻ Refrescar</button>
        </div>

        {error && <p className="error">Error: {error}</p>}

        {globalCfg && (
          <table className="data">
            <tbody>
              <tr>
                <th>Host global</th>
                <td><code>{globalCfg.host}</code></td>
              </tr>
              <tr>
                <th>Verify SSL</th>
                <td>{globalCfg.verifySsl ? "true" : "false"}</td>
              </tr>
              <tr>
                <th>Timeout</th>
                <td>{globalCfg.timeoutMs} ms</td>
              </tr>
              <tr>
                <th>Page size OData</th>
                <td>{globalCfg.pageSize}</td>
              </tr>
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 16 }}>
          <label>
            Empresa:{" "}
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">— selecciona —</option>
              {(companies ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.sap ? "✅" : "⚪"} {c.alias || c.name} ({c.id})
                  {c.sap ? ` · ${c.sap.companyDb}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {selected && (
        <CompanySapPanel
          key={selected.id}
          company={selected}
          onChanged={loadAll}
        />
      )}
    </>
  );
}

function CompanySapPanel({
  company,
  onChanged,
}: {
  company: SapCompanyListItem;
  onChanged: () => void;
}) {
  const [status, setStatus] = useState<SapCompanyStatus | null>(null);
  const [ping, setPing] = useState<SapPingResponse | null>(null);
  const [pingBusy, setPingBusy] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);

  const refreshStatus = () => {
    sapCompanyStatus(company.id).then(setStatus).catch(() => setStatus(null));
  };
  useEffect(refreshStatus, [company.id]);

  const doPing = async () => {
    setPingBusy(true);
    setPing(null);
    try {
      const res = await sapCompanyPing(company.id);
      setPing(res);
      refreshStatus();
      onChanged();
    } catch (e: unknown) {
      setPing({ ok: false, error: e instanceof Error ? e.message : String(e) });
    } finally {
      setPingBusy(false);
    }
  };

  const doLogout = async () => {
    setLogoutBusy(true);
    try {
      await sapCompanyLogout(company.id);
      setPing(null);
      refreshStatus();
      onChanged();
    } finally {
      setLogoutBusy(false);
    }
  };

  return (
    <>
      <SapConfigCard company={company} onSaved={onChanged} />

      <section className="card">
        <div className="card-head">
          <h2>Conexión de {company.alias || company.name}</h2>
          <button type="button" onClick={refreshStatus}>↻ Estado</button>
        </div>

        {!company.sap && (
          <p className="meta">
            Configura la conexión SAP para esta empresa arriba antes de probar.
          </p>
        )}

        {company.sap && status && (
          <table className="data">
            <tbody>
              <tr><th>CompanyDB</th><td><code>{status.companyDb}</code></td></tr>
              <tr>
                <th>Sesión</th>
                <td>
                  {status.connected ? "✅ conectada" : "⚪ sin sesión"}
                  {status.sessionIdPreview && (
                    <> · SessionId <code>{status.sessionIdPreview}</code></>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button type="button" onClick={doPing} disabled={pingBusy || !company.sap}>
            {pingBusy ? "Conectando…" : "Probar conexión (Login)"}
          </button>
          <button
            type="button"
            onClick={doLogout}
            disabled={logoutBusy || !status?.connected}
          >
            {logoutBusy ? "Cerrando…" : "Cerrar sesión (Logout)"}
          </button>
        </div>

        {ping && (
          <p className={ping.ok ? "meta" : "error"} style={{ marginTop: 12 }}>
            {ping.ok ? (
              <>
                ✅ Login OK · CompanyDB <code>{ping.companyDb}</code> · usuario{" "}
                <code>{ping.username}</code> · SessionId <code>{ping.sessionIdPreview}</code>
              </>
            ) : (
              <>❌ {ping.error}</>
            )}
          </p>
        )}
      </section>

      {company.sap && <OdataExplorer companyId={company.id} />}
    </>
  );
}

function SapConfigCard({
  company,
  onSaved,
}: {
  company: SapCompanyListItem;
  onSaved: () => void;
}) {
  const [companyDb, setCompanyDb] = useState(company.sap?.companyDb ?? "");
  const [username, setUsername] = useState(company.sap?.username ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    setCompanyDb(company.sap?.companyDb ?? "");
    setUsername(company.sap?.username ?? "");
    setPassword("");
    setMsg(null);
  }, [company.id, company.sap?.companyDb, company.sap?.username]);

  const save = async () => {
    if (!companyDb.trim() || !username.trim()) {
      setMsg({ kind: "err", text: "CompanyDB y usuario son obligatorios." });
      return;
    }
    if (!company.sap && !password) {
      setMsg({ kind: "err", text: "Password obligatoria en el primer alta." });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await sapSetCompanyConfig(company.id, {
        companyDb: companyDb.trim(),
        username: username.trim(),
        password: password || undefined,
      });
      setPassword("");
      setMsg({ kind: "ok", text: "Configuración guardada." });
      onSaved();
    } catch (e: unknown) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!confirm(`Eliminar la configuración SAP de ${company.alias || company.name}?`)) return;
    setBusy(true);
    try {
      await sapClearCompanyConfig(company.id);
      setMsg({ kind: "ok", text: "Configuración eliminada." });
      onSaved();
    } catch (e: unknown) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <h2>Configuración SAP de {company.alias || company.name}</h2>
        {company.sap && (
          <button type="button" className="danger" onClick={clear} disabled={busy}>
            🗑️ Eliminar configuración
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label>
          CompanyDB
          <input
            type="text"
            value={companyDb}
            onChange={(e) => setCompanyDb(e.target.value)}
            placeholder="B1_EMO_ES"
          />
        </label>
        <label>
          Usuario
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="manager"
          />
        </label>
        <label style={{ gridColumn: "1 / span 2" }}>
          Password{" "}
          {company.sap?.hasPassword && (
            <span className="meta" style={{ fontSize: "0.85em" }}>
              (dejar vacío para conservar la actual)
            </span>
          )}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={company.sap?.hasPassword ? "•••••• (existente)" : "password"}
            autoComplete="new-password"
          />
        </label>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button type="button" onClick={save} disabled={busy}>
          {busy ? "Guardando…" : company.sap ? "Guardar cambios" : "Guardar y activar"}
        </button>
      </div>

      {msg && (
        <p className={msg.kind === "ok" ? "meta" : "error"} style={{ marginTop: 12 }}>
          {msg.text}
        </p>
      )}
    </section>
  );
}

function OdataExplorer({ companyId }: { companyId: string }) {
  const [entity, setEntity] = useState("BusinessPartners");
  const [top, setTop] = useState<number>(10);
  const [skip, setSkip] = useState<number>(0);
  const [filter, setFilter] = useState("");
  const [select, setSelect] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [expand, setExpand] = useState("");
  const [query, setQuery] = useState<QueryState>({ kind: "idle" });

  useEffect(() => {
    // reset al cambiar de empresa
    setQuery({ kind: "idle" });
    setSkip(0);
  }, [companyId]);

  const runQuery = async () => {
    if (!entity.trim()) return;
    setQuery({ kind: "loading" });
    try {
      const data = await sapCompanyEntity(companyId, entity.trim(), {
        top,
        skip,
        filter: filter.trim() || undefined,
        select: select.trim() || undefined,
        orderBy: orderBy.trim() || undefined,
        expand: expand.trim() || undefined,
      });
      setQuery({ kind: "ok", data });
    } catch (e: unknown) {
      setQuery({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };

  const columns =
    query.kind === "ok" && query.data.value.length > 0
      ? Array.from(
          query.data.value.reduce<Set<string>>((acc, row) => {
            for (const k of Object.keys(row)) if (!k.startsWith("@odata")) acc.add(k);
            return acc;
          }, new Set()),
        )
      : [];

  return (
    <section className="card">
      <div className="card-head">
        <h2>Explorador OData</h2>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label>
          Entidad
          <input
            type="text"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            placeholder="BusinessPartners"
          />
        </label>
        <label>
          $filter
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="CardType eq 'cCustomer'"
          />
        </label>
        <label>
          $select
          <input
            type="text"
            value={select}
            onChange={(e) => setSelect(e.target.value)}
            placeholder="CardCode,CardName,CardType"
          />
        </label>
        <label>
          $orderby
          <input
            type="text"
            value={orderBy}
            onChange={(e) => setOrderBy(e.target.value)}
            placeholder="CardCode desc"
          />
        </label>
        <label style={{ gridColumn: "1 / span 2" }}>
          $expand
          <input
            type="text"
            value={expand}
            onChange={(e) => setExpand(e.target.value)}
            placeholder="JournalEntryLines"
          />
        </label>
        <label>
          $top
          <input
            type="number"
            min={1}
            max={1000}
            value={top}
            onChange={(e) => setTop(Number(e.target.value) || 1)}
          />
        </label>
        <label>
          $skip
          <input
            type="number"
            min={0}
            value={skip}
            onChange={(e) => setSkip(Number(e.target.value) || 0)}
          />
        </label>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button type="button" onClick={runQuery} disabled={query.kind === "loading"}>
          {query.kind === "loading" ? "Consultando…" : "Consultar"}
        </button>
        {query.kind === "ok" && query.data.nextSkip !== null && (
          <button
            type="button"
            onClick={() => setSkip(query.data.nextSkip ?? skip)}
          >
            Siguiente página →
          </button>
        )}
      </div>

      {query.kind === "error" && <p className="error" style={{ marginTop: 12 }}>Error: {query.message}</p>}

      {query.kind === "ok" && (
        <>
          <p className="meta" style={{ marginTop: 12 }}>
            {query.data.value.length} filas devueltas · top={query.data.top} · skip={query.data.skip}
            {query.data.nextSkip !== null && <> · hay más resultados (nextSkip={query.data.nextSkip})</>}
          </p>
          {query.data.value.length > 0 && columns.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="data">
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {query.data.value.map((row, i) => (
                    <tr key={i}>
                      {columns.map((c) => (
                        <td key={c}>
                          <code>{formatCell(row[c])}</code>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <details style={{ marginTop: 12 }}>
            <summary>Ver JSON crudo</summary>
            <pre style={{ maxHeight: 400, overflow: "auto" }}>
              {JSON.stringify(query.data.value, null, 2)}
            </pre>
          </details>
        </>
      )}
    </section>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
